import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Breadcrumb, Row, Col, Card, Spin } from 'antd';
import * as actions from '~actions/billing';
import generatePath from '~settings/routing';
import formatPrice from '~helpers/currency';
import { communicationStatus } from '~client/constants';
import { allPaymentMethod } from '~constants/landlord';
import RefundModal from '~pages/dashboard/billing/refund/modal';
import { camelCaseToUpperFirstCase } from '~helpers';
import { refundStatus, refundFromTypes } from '~constants/landlord';
import { Link } from 'react-router-dom';
import RejectModal from '../reject-modal';

const mapStateToProps = state => ({
  details: state.dashboard.billing.get('refundDetails')?.toJS(),
  communication: state.dashboard.billing.get('communication')?.toJS(),
});

const mapDispatchToProps = dispatch => ({
  orderRefund: params => {
    dispatch(actions.orderRefund(params));
  },
  updateOrderReceivable: (params, successCallback) => {
    dispatch(actions.updateOrderReceivable(params, successCallback));
  },
  financialRefund: (params, successCallback, type) => {
    dispatch(actions.financialRefund(params, successCallback, type));
  },
  refundConfirm: (params, successCallback) => {
    dispatch(actions.refundConfirm(params, successCallback));
  },
});

@connect(mapStateToProps, mapDispatchToProps)
@withTranslation()
export default class RefundDetails extends React.Component {
  getRefundDetails = () => {
    const params = this.props.match.params;
    this.props.orderRefund(params);
  };

  componentDidMount() {
    this.getRefundDetails();
  }

  updateRefundStatus = type => {
    const { details } = this.props;
    const data = { id: details.id };

    if (type === 'confirm') {
      this.props.refundConfirm(data, () => {
        this.getRefundDetails();
      });
    } else {
      this.props.financialRefund(
        data,
        () => {
          this.getRefundDetails();
        },
        type,
      );
    }
  };

  handleRefundDetailData = details => {
    const arrKeysList = [];
    if (details) {
      const keysList = Object.keys(details);
      while (keysList.length > 0) {
        arrKeysList.push(keysList.splice(0, 2));
      }
    }

    return arrKeysList;
  };

  render() {
    const { t, details, communication } = this.props;
    const paymentMethodIsStripe = details.refundMethod === allPaymentMethod.STRIPE;
    const refundIsPending =
      details.status === refundStatus.PENDING_REFUND &&
      details.refundFrom === refundFromTypes.STCOM;
    const apIsPending =
      paymentMethodIsStripe &&
      refundStatus.PENDING_REFUND === details.status &&
      details.refundFrom === refundFromTypes.ACCOMMODATION_PARTNER;
    const apIsConfirmation =
      !paymentMethodIsStripe &&
      refundStatus.REFUND_CONFIRMATION === details.status &&
      details.refundFrom === refundFromTypes.ACCOMMODATION_PARTNER;

    let modalType;

    if (apIsPending) {
      modalType = 'refund';
    } else if (refundIsPending) {
      modalType = 'refunded';
    } else if (apIsConfirmation) {
      modalType = 'confirm';
    }

    return (
      <div className="refund-details">
        <Choose>
          <When condition={communication.refundDetails.status === communicationStatus.FETCHING}>
            <div className="refund-details__loading">
              <Spin />
            </div>
          </When>
          <Otherwise>
            <div className="refund__header">
              <div className="refund-details__bread-crumb">
                <Breadcrumb>
                  <Breadcrumb.Item>
                    <Link className="refund__bread-crumb-text" to={generatePath('billing', {})}>
                      {t('cms.billing.transfer.header.menu_link.billing')}
                    </Link>
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>
                    <Link
                      className="refund__bread-crumb-text"
                      to={generatePath('billing.refunds', {})}
                    >
                      {t('cms.billing.transfer.header.menu_link.refund')}
                    </Link>
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>
                    <span className="refund__bread-crumb-text">
                      {t('cms.billing.transfer.header.menu_link.refund.details')}
                    </span>
                  </Breadcrumb.Item>
                </Breadcrumb>
              </div>
              <h2 className="refund-details__title">
                {t('cms.billing.refund_details.header.title', {
                  id: details.referenceId,
                })}
              </h2>

              <div className="refund-details__header-content--left">
                <div className="refund-details__header-content-btns">
                  <If
                    condition={
                      details.refundType === 'DEPOSIT' &&
                      details.status === refundStatus.PENDING_REFUND &&
                      (details.refundFrom === refundFromTypes.STCOM ||
                        (details.refundMethod === allPaymentMethod.STRIPE &&
                          details.refundFrom === refundFromTypes.ACCOMMODATION_PARTNER))
                    }
                  >
                    <RejectModal
                      t={t}
                      refundDetails={details}
                      modalType={modalType}
                      onSuccess={this.getRefundDetails}
                    />
                  </If>
                  <If condition={refundIsPending || apIsPending || apIsConfirmation}>
                    <RefundModal
                      t={t}
                      modalType={modalType}
                      communication={this.props.communication}
                      refundDetails={details}
                      onConfirm={() => this.updateRefundStatus(modalType)}
                    />
                  </If>
                </div>
                <div className="refund-details__right-info">
                  <div className="refund-details__right-row">
                    <span className="refund-details__info-right">
                      {t('cms.billing.refund.details.refund_status.field')}
                    </span>
                    <span className="refund-details__info-right--value">
                      {details.status
                        ? t(
                            `cms.billing.refund.table.refund_status.${details.status.toLowerCase()}`,
                          )
                        : '-'}
                    </span>
                  </div>
                  <div className="refund-details__right-row">
                    <span className="refund-details__info-right">
                      {t('cms.billing.refund.details.refund_price.field')}
                    </span>
                    <span className="refund-details__info-right--value">
                      {details.amount && details.currency
                        ? formatPrice(details.amount, details.currency, 2)
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="refund-details__header-content">
                <Row className="refund-details__info-row" gutter={50}>
                  <Col span={12} className="refund-details__info-col">
                    <span className="refund-details__info">
                      {t('cms.billing.refund.details.refund_type.field', {
                        value: details.refundType
                          ? t(
                              `cms.billing.refund.table.refund_type.${details.refundType.toLowerCase()}`,
                            )
                          : '-',
                      })}
                    </span>
                  </Col>
                  <Col span={12} className="refund-details__info-col">
                    <span className="refund-details__info">
                      {t('cms.billing.refund.details.refund_method.field', {
                        value: details.refundMethod
                          ? t(
                              `cms.billing.refund.table.refund_method.${details.refundMethod.toLowerCase()}`,
                            )
                          : '-',
                      })}
                    </span>
                  </Col>
                </Row>
                <Row className="refund-details__info-row" gutter={50}>
                  <Col span={12} className="refund-details__info-col">
                    <span className="refund-details__info">
                      {t('cms.billing.refund.details.landlord.field', {
                        value:
                          details.landlord && details.landlord.name ? details.landlord.name : '-',
                      })}
                    </span>
                  </Col>
                  <Col span={12} className="refund-details__info-col">
                    <span className="refund-details__info">
                      {t('cms.billing.refund.details.property.field', {
                        value:
                          details.order && details.order.property && details.order.property.name
                            ? details.order.property.name
                            : '-',
                      })}
                    </span>
                  </Col>
                </Row>
                <Row className="refund-details__info-row" gutter={50}>
                  <Col span={12} className="refund-details__info-col">
                    <span className="refund-details__info">
                      {t('cms.billing.refund.details.booking_number.field', {
                        value:
                          details.order && details.order.referenceId
                            ? details.order.referenceId
                            : '-',
                      })}
                    </span>
                  </Col>
                  <Col span={12} className="refund-details__info-col">
                    <span className="refund-details__info">
                      {t('cms.billing.refund.details.payment_amount.field', {
                        value:
                          details.orderPayment && details.orderPayment.price
                            ? formatPrice(
                                details.orderPayment.price,
                                details.orderPayment.currency,
                                2,
                              )
                            : '-',
                      })}
                    </span>
                  </Col>
                </Row>
                <Row gutter={50}>
                  <Col span={12} className="refund-details__info-col">
                    <span className="refund-details__info">
                      {t('cms.billing.refund.details.refund_from.field', {
                        value: details.refundFrom
                          ? t(
                              `cms.billing.refund.table.refund_from.${
                                modalType === 'refund'
                                  ? 'landlord'
                                  : details.refundFrom.toLowerCase()
                              }`,
                            )
                          : '-',
                      })}
                    </span>
                  </Col>
                  <Col span={12} className="refund-details__info-col">
                    <span className="refund-details__info">
                      {t('cms.billing.refund.details.refund_to.field', {
                        value: details.refundTo
                          ? t(
                              `cms.billing.refund.table.refund_to.${details.refundTo.toLowerCase()}`,
                            )
                          : '-',
                      })}
                    </span>
                  </Col>
                </Row>
              </div>
            </div>

            <div className="refund-details__content">
              <Card title={t('cms.billing.refund.details.refund_info.title')}>
                <div className="refund-details__box">
                  <p className="refund-details__info">
                    {t('cms.billing.refund.details.refund_amount.field', {
                      value: details.amount
                        ? formatPrice(details.amount, details.currency, 2)
                        : '-',
                    })}
                  </p>
                  <p className="refund-details__info">
                    {t('cms.billing.refund.details.refund_method.field', {
                      value: details.refundMethod
                        ? t(
                            `cms.billing.refund.table.refund_method.${details.refundMethod.toLowerCase()}`,
                          )
                        : '-',
                    })}
                  </p>
                  <If condition={details.refundMethod === 'CARD' && details.refundMethodDetails}>
                    <p className="refund-details__info">
                      {t('cms.billing.refund.details.stripe_account_name.field', {
                        value: details.refundMethodDetails.stripeConnectedName
                          ? details.refundMethodDetails.stripeConnectedName
                          : '-',
                      })}
                    </p>
                    <p className="refund-details__info">
                      {t('cms.billing.refund.details.stripe_account_id.field', {
                        value: details.refundMethodDetails.destination
                          ? details.refundMethodDetails.destination
                          : '-',
                      })}
                    </p>
                  </If>
                  <p className="refund-details__info">
                    {t('cms.billing.refund.details.trade_number.field', {
                      value: details?.transactionNo ?? '-',
                    })}
                  </p>
                  <div style={{ width: '100%', marginBottom: 12 }}>
                    <If condition={details.refundFrom === 'ACCOMMODATION_PARTNER'}>
                      <p className="refund-details__info">
                        {t('cms.billing.refund.details.refund_method_details.field')}
                        {details.refundMethodDetails ? '' : ': -'}
                      </p>
                      <If condition={details.refundMethodDetails}>
                        <For
                          of={this.handleRefundDetailData(details.refundMethodDetails)}
                          each="items"
                          index="key"
                        >
                          <Row key={key} style={{ width: '100%', marginBottom: 12 }}>
                            <For of={items} each="item" index="index">
                              <Col span={12} key={index}>
                                <span className="refund-modal__field-name">
                                  {`${camelCaseToUpperFirstCase(item)}: ${
                                    details.refundMethodDetails[item] || '-'
                                  }`}
                                </span>
                              </Col>
                            </For>
                          </Row>
                        </For>
                      </If>
                    </If>
                  </div>
                </div>{' '}
              </Card>
            </div>
            <div className="refund-details__content" style={{ marginTop: '24px' }}>
              <Card title="Cancellation Reason">
                <div className="refund-details__box">
                  <p className="refund-details__info">
                    {t('cms.billing.refund.details.cancel_reason.field', {
                      value: details.order?.cancelledReason,
                    })}
                  </p>
                  <p className="refund-details__info">
                    {t('cms.billing.refund.details.isPaid.field', {
                      value: details.order?.isPaidToLandlord
                        ? t('cms.referral.payout.cancel_reason.editor.btn.yes')
                        : t('cms.referral.payout.cancel_reason.editor.btn.no'),
                    })}
                  </p>
                  <p className="refund-details__info">
                    {t('cms.billing.refund.details.comment.field', {
                      value: details.order?.cancellationNote
                        ? details.order?.cancellationNote
                        : '-',
                    })}
                  </p>
                </div>
              </Card>
            </div>
            <If condition={details.rejectReason}>
              <div className="refund-details__content" style={{ marginTop: '24px' }}>
                <Card title="Reject Reason">
                  <div className="refund-details__box">
                    <p className="refund-details__info">
                      {t('cms.refund.reject_reason', { reason: details.rejectReason })}
                    </p>
                  </div>
                </Card>
              </div>
            </If>
          </Otherwise>
        </Choose>
      </div>
    );
  }
}

RefundDetails.propTypes = {
  t: PropTypes.func.isRequired,
  orderRefund: PropTypes.func,
  details: PropTypes.object,
  match: PropTypes.object,
  communication: PropTypes.object,
  financialRefund: PropTypes.func.isRequired,
  refundConfirm: PropTypes.func.isRequired,
};

RefundDetails.defaultProps = {
  t: () => {},
  orderRefund: () => {},
  details: {},
  match: {},
  communication: {},
  financialRefund: () => {},
  refundConfirm: () => {},
};
